function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function createSPCLSTCartDebug() {
  const storageKey = 'spclst:cart-debug';
  const queryKey = 'spclst_cart_debug';

  const setEnabled = (isEnabled) => {
    try {
      if (isEnabled) {
        localStorage.setItem(storageKey, '1');
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.warn('[SPCLST cart debug] Unable to update debug flag.', error);
    }
  };

  try {
    const queryValue = new URLSearchParams(window.location.search).get(queryKey);
    if (queryValue === '1') setEnabled(true);
    if (queryValue === '0') setEnabled(false);
  } catch (error) {
    console.warn('[SPCLST cart debug] Unable to read debug query flag.', error);
  }

  const enabled = () => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch (error) {
      return false;
    }
  };

  const getStorageAge = (key) => {
    try {
      const value = localStorage.getItem(key);
      if (!value) return null;
      const timestamp = Number(value);
      return Number.isFinite(timestamp) ? Date.now() - timestamp : 'invalid';
    } catch (error) {
      return 'unavailable';
    }
  };

  const summarizeToken = (token) => {
    if (!token || typeof token !== 'string') return null;
    return {
      present: true,
      length: token.length,
      tail: token.slice(-8),
    };
  };

  const decodeCookieValue = (value) => {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return String(value || '');
    }
  };

  const summarizeCartCookieValues = (values) => {
    const normalizedValues = values.map((value) => decodeCookieValue(value));
    const distinctValueCount = new Set(normalizedValues).size;

    return {
      cartCookieKeyCount: normalizedValues.filter((value) => value.indexOf('?key=') > -1).length,
      cartCookieDistinctValueCount: distinctValueCount,
      cartCookieValuesMatch: normalizedValues.length > 1 ? distinctValueCount === 1 : null,
    };
  };

  const getCookieState = () => {
    try {
      const cookies = document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .filter(Boolean);
      const cookieNames = cookies.map((cookie) => cookie.split('=')[0].trim());
      const cartCookieNames = cookieNames.filter((name) => /cart|shopify/i.test(name));
      const cartCookies = cookies.filter((cookie) => cookie.indexOf('cart=') === 0);
      const cartCookie = cartCookies[0];
      const cartCookieValue = cartCookie ? decodeCookieValue(cartCookie.slice(5)) : '';
      const cartCookieShape = summarizeCartCookieValues(cartCookies.map((cookie) => cookie.slice(5)));

      return {
        count: cookieNames.length,
        cartCookieNames,
        cartCookieCount: cartCookies.length,
        hasCartCookie: cookieNames.includes('cart'),
        cartCookieHasKey: cartCookieValue.indexOf('?key=') > -1,
        duplicateCartCookie: cartCookies.length > 1,
        ...cartCookieShape,
        cartCookieSummary: summarizeToken(cartCookieValue),
      };
    } catch (error) {
      return { error: error.message };
    }
  };

  const getFailureCookieStoreState = () => {
    const cookieStoreApi = window.cookieStore;
    if (!cookieStoreApi || typeof cookieStoreApi.getAll !== 'function') {
      return Promise.resolve({ supported: false, readSucceeded: false, timedOut: false });
    }

    const timeoutState = { supported: true, readSucceeded: false, timedOut: true };
    const timeout = new Promise((resolve) => window.setTimeout(() => resolve(timeoutState), 150));
    const read = Promise.resolve()
      .then(() => cookieStoreApi.getAll('cart'))
      .then((cookies) => {
        const cartCookies = Array.isArray(cookies) ? cookies : [];
        const values = cartCookies.map((cookie) => String((cookie && cookie.value) || ''));
        const cookieShape = summarizeCartCookieValues(values);
        const scopeMetadataAvailable =
          cartCookies.length > 0 &&
          cartCookies.every((cookie) => cookie && 'domain' in cookie && 'path' in cookie);
        const securityMetadataAvailable =
          cartCookies.length > 0 &&
          cartCookies.every((cookie) => cookie && 'secure' in cookie && 'partitioned' in cookie);
        const scopes = scopeMetadataAvailable
          ? new Set(
              cartCookies.map((cookie) => {
                const domain = typeof cookie.domain === 'string' ? cookie.domain : '';
                const path = typeof cookie.path === 'string' ? cookie.path : '/';
                return `${domain || '[host-only]'}|${path || '/'}`;
              })
            )
          : null;
        const emptyOrUnavailable = (value) => (cartCookies.length === 0 ? 0 : value);

        return {
          supported: true,
          readSucceeded: true,
          timedOut: false,
          cartCookieCount: cartCookies.length,
          ...cookieShape,
          scopeMetadataAvailable,
          hostOnlyCount: emptyOrUnavailable(
            scopeMetadataAvailable ? cartCookies.filter((cookie) => !cookie.domain).length : null
          ),
          domainScopedCount: emptyOrUnavailable(
            scopeMetadataAvailable ? cartCookies.filter((cookie) => !!cookie.domain).length : null
          ),
          rootPathCount: emptyOrUnavailable(
            scopeMetadataAvailable ? cartCookies.filter((cookie) => !cookie.path || cookie.path === '/').length : null
          ),
          nonRootPathCount: emptyOrUnavailable(
            scopeMetadataAvailable ? cartCookies.filter((cookie) => !!cookie.path && cookie.path !== '/').length : null
          ),
          distinctScopeCount: emptyOrUnavailable(scopes ? scopes.size : null),
          securityMetadataAvailable,
          secureCount: emptyOrUnavailable(
            securityMetadataAvailable ? cartCookies.filter((cookie) => cookie.secure === true).length : null
          ),
          partitionedCount: emptyOrUnavailable(
            securityMetadataAvailable ? cartCookies.filter((cookie) => cookie.partitioned === true).length : null
          ),
        };
      })
      .catch(() => ({ supported: true, readSucceeded: false, timedOut: false }));

    return Promise.race([read, timeout]);
  };

  const getPrivacyState = () => {
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!customerPrivacy || typeof customerPrivacy.currentVisitorConsent !== 'function') return null;

    try {
      return customerPrivacy.currentVisitorConsent();
    } catch (error) {
      return { error: error.message };
    }
  };

  const getShopCartSyncState = () => {
    const initFunction = window.Shopify && window.Shopify.SignInWithShop
      ? window.Shopify.SignInWithShop.initShopCartSync
      : undefined;
    const guard = window.SPCLSTShopCartSyncGuard;
    let guardStatus = null;

    try {
      guardStatus = guard && typeof guard.status === 'function' ? guard.status() : guard || null;
    } catch (error) {
      guardStatus = { error: error.message };
    }

    return {
      elementPresent: !!document.querySelector('shop-cart-sync'),
      iframePresent: !!document.querySelector('[data-testid="shop-cart-sync-iframe"], iframe[title="Shop Pay cart sync"]'),
      initType: typeof initFunction,
      attemptedAgeMs: getStorageAge('signInWithShop:cartSyncTransferAttemptedAt'),
      completedAgeMs: getStorageAge('signInWithShop:cartSyncTransferCompletedAt'),
      blockedInitCount: Number((guardStatus && guardStatus.blockedInitCount) || 0),
      removedNodeCount: Number((guardStatus && guardStatus.removedNodeCount) || 0),
      clearedStorageKeyCount: Array.isArray(guardStatus && guardStatus.clearedStorageKeys)
        ? guardStatus.clearedStorageKeys.length
        : 0,
      guard: guardStatus,
    };
  };

  const getPageState = () => ({
    href: window.location.href,
    host: window.location.host,
    theme: window.Shopify && window.Shopify.theme
      ? {
          id: window.Shopify.theme.id || null,
          name: window.Shopify.theme.name || null,
          role: window.Shopify.theme.role || null,
        }
      : null,
    cookieEnabled: navigator.cookieEnabled,
    cookies: getCookieState(),
    customerPrivacy: getPrivacyState(),
    shopCartSync: getShopCartSyncState(),
  });

  const summarizeSections = (sections) => {
    if (!sections) return null;

    return Object.keys(sections).reduce((summary, sectionId) => {
      const html = sections[sectionId];
      const isString = typeof html === 'string';
      summary[sectionId] = {
        isNull: html === null,
        present: isString && html.length > 0,
        length: isString ? html.length : 0,
        cartDrawerRows: isString ? (html.match(/id="CartDrawer-Item-/g) || []).length : 0,
      };
      return summary;
    }, {});
  };

  const summarizeCartJson = (cart) => {
    if (!cart) return null;

    return {
      item_count: Number(cart.item_count || 0),
      total_price: cart.total_price,
      currency: cart.currency,
      token: summarizeToken(cart.token),
      items: Array.isArray(cart.items)
        ? cart.items.map((item) => ({
            id: item.id,
            variant_id: item.variant_id,
            title: item.title,
            quantity: item.quantity,
          }))
        : [],
    };
  };

  const log = (label, details = {}) => {
    if (!enabled()) return;

    const payload = {
      time: new Date().toISOString(),
      page: getPageState(),
      ...details,
    };
    let serializedPayload = '';

    try {
      serializedPayload = JSON.stringify(payload);
    } catch (error) {
      serializedPayload = JSON.stringify({
        time: payload.time,
        serializationError: error.message,
      });
    }

    if (console.groupCollapsed) {
      console.groupCollapsed(`[SPCLST cart debug] ${label}`);
      console.log(payload);
      console.log(`[SPCLST cart debug json] ${label} ${serializedPayload}`);
      console.groupEnd();
      return;
    }

    console.log(`[SPCLST cart debug] ${label}`, payload);
    console.log(`[SPCLST cart debug json] ${label} ${serializedPayload}`);
  };

  const fetchCartState = () => {
    const url = new URL('/cart.js', window.location.origin);
    url.searchParams.set('_', Date.now().toString());

    return fetch(url.toString(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) =>
        response.text().then((text) => {
          let cart = null;
          let parseError = null;

          try {
            cart = JSON.parse(text);
          } catch (error) {
            parseError = error.message;
          }

          return {
            ok: response.ok,
            status: response.status,
            contentType: response.headers.get('content-type') || '',
            parsed: !!cart,
            parseError,
            textStart: cart ? null : text.slice(0, 200),
            cart: summarizeCartJson(cart),
          };
        })
      )
      .catch((error) => ({
        networkError: error.message,
      }));
  };

  const logCartState = (label) => {
    if (!enabled()) return Promise.resolve();
    return fetchCartState().then((cartState) => {
      log(label, { cartState });
    });
  };

  return {
    enabled,
    log,
    logCartState,
    fetchCartState,
    getPageState,
    getFailureCookieStoreState,
    summarizeSections,
    summarizeCartJson,
    summarizeToken,
  };
}

window.SPCLSTCartDebug = window.SPCLSTCartDebug || createSPCLSTCartDebug();
if (window.SPCLSTCartDebug.enabled()) {
  window.SPCLSTCartDebug.log('page ready');
  window.SPCLSTCartDebug.logCartState('cart state on page ready');
}

function createSPCLSTCartMonitor() {
  const storageKey = 'spclst:last-known-non-empty-cart';
  const monitorVersion = '2026-08-03.1';

  const toItemCount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const count = Number(value);
    return Number.isFinite(count) && count >= 0 ? Math.trunc(count) : null;
  };

  const readElementCount = (selector) => {
    try {
      const element = document.querySelector(selector);
      if (!element) return null;
      const match = String(element.textContent || '').match(/\d+/);
      return match ? toItemCount(match[0]) : null;
    } catch (error) {
      return null;
    }
  };

  const readRenderedCartCounts = () => {
    const drawerItemCount = readElementCount('.spclst-cart-drawer-count');
    const desktopHeaderItemCount = readElementCount('#cart-icon-bubble .cartcount');
    const mobileHeaderItemCount = readElementCount('[data-spclst-open-cart-drawer] .mobilecartcount');
    const headerItemCount = desktopHeaderItemCount ?? mobileHeaderItemCount;
    const baselineCountConflict =
      drawerItemCount !== null && headerItemCount !== null && drawerItemCount !== headerItemCount;

    return {
      drawerItemCount,
      headerItemCount,
      displayedItemCount: baselineCountConflict ? null : drawerItemCount ?? headerItemCount,
      baselineCountConflict,
    };
  };

  const summarizeCart = (cart) => {
    const debug = window.SPCLSTCartDebug;
    if (debug && typeof debug.summarizeCartJson === 'function') return debug.summarizeCartJson(cart);
    return cart ? { item_count: Number(cart.item_count || 0) } : null;
  };

  const getPageState = () => {
    const debug = window.SPCLSTCartDebug;
    if (debug && typeof debug.getPageState === 'function') {
      const pageState = debug.getPageState();
      const cookies = pageState.cookies || {};
      const shopCartSync = pageState.shopCartSync || {};

      return {
        href: window.location.pathname,
        host: pageState.host,
        theme: pageState.theme,
        cookieEnabled: pageState.cookieEnabled,
        cookies: {
          hasCartCookie: cookies.hasCartCookie,
          cartCookieCount: cookies.cartCookieCount,
          cartCookieHasKey: cookies.cartCookieHasKey,
          duplicateCartCookie: cookies.duplicateCartCookie,
          cartCookieKeyCount: cookies.cartCookieKeyCount,
          cartCookieDistinctValueCount: cookies.cartCookieDistinctValueCount,
          cartCookieValuesMatch: cookies.cartCookieValuesMatch,
        },
        shopCartSync: {
          elementPresent: shopCartSync.elementPresent,
          iframePresent: shopCartSync.iframePresent,
          initType: shopCartSync.initType,
          blockedInitCount: shopCartSync.blockedInitCount,
          removedNodeCount: shopCartSync.removedNodeCount,
          clearedStorageKeyCount: shopCartSync.clearedStorageKeyCount,
        },
      };
    }
    return {
      href: window.location.pathname,
      host: window.location.host,
      cookieEnabled: navigator.cookieEnabled,
    };
  };

  const readLastKnownCart = () => {
    try {
      const rawValue = sessionStorage.getItem(storageKey);
      return rawValue ? JSON.parse(rawValue) : null;
    } catch (error) {
      return null;
    }
  };

  const writeLastKnownCart = (cart, context = {}) => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          time: new Date().toISOString(),
          source: context.source || 'cart-state',
          cart: summarizeCart(cart),
        })
      );
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; monitoring should never block cart behavior.
    }
  };

  const clearLastKnownCart = () => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; monitoring should never block cart behavior.
    }
  };

  const createEventPayload = (eventName, details = {}) => ({
    time: new Date().toISOString(),
    eventName,
    monitorVersion,
    userAgent: navigator.userAgent,
    page: getPageState(),
    ...details,
  });

  const publishPayload = (payload) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('spclst_cart_failure', payload);
      }
    } catch (error) {
      // Ignore pixel bridge failures.
    }
  };

  const reportRecovery = (eventName, details = {}) => {
    publishPayload(createEventPayload(eventName, details));
  };

  const reportFailure = (eventName, details = {}) => {
    const payload = createEventPayload(eventName, details);

    console.warn(`[SPCLST cart monitor] ${eventName}`, payload);

    const debug = window.SPCLSTCartDebug;
    if (!debug || typeof debug.getFailureCookieStoreState !== 'function') {
      publishPayload(payload);
      return;
    }

    Promise.resolve()
      .then(() => debug.getFailureCookieStoreState())
      .then((cookieStoreState) => {
        if (!payload.page.cookies || typeof payload.page.cookies !== 'object') payload.page.cookies = {};
        payload.page.cookies.cookieStore = cookieStoreState;
      })
      .catch(() => {})
      .then(() => publishPayload(payload));
  };

  const captureAddBaseline = (context = {}) => {
    const requestedQuantity = Math.max(1, toItemCount(context.requestedQuantity) ?? 1);
    const renderedCounts = readRenderedCartCounts();
    const lastKnownCart = readLastKnownCart();
    const storedItemCount = toItemCount(lastKnownCart && lastKnownCart.cart && lastKnownCart.cart.item_count);
    const previousItemCount = renderedCounts.baselineCountConflict
      ? null
      : renderedCounts.displayedItemCount ?? storedItemCount;
    const baselineSource = renderedCounts.baselineCountConflict
      ? 'rendered_count_conflict'
      : renderedCounts.displayedItemCount !== null
        ? 'rendered_cart_count'
        : storedItemCount !== null
          ? 'last_known_cart'
          : 'unavailable';
    const expectedItemCount = previousItemCount === null ? null : previousItemCount + requestedQuantity;

    if (renderedCounts.baselineCountConflict) {
      reportFailure('cart_surface_count_mismatch_before_add', {
        source: context.source || 'product-form',
        operation: 'add-baseline',
        requestedQuantity,
        previousItemCount: null,
        expectedItemCount: null,
        baselineSource,
        baselineCountConflict: true,
        drawerItemCount: renderedCounts.drawerItemCount,
        headerItemCount: renderedCounts.headerItemCount,
        storedItemCount,
        lastKnownCart,
        currentCart: null,
      });
    }

    return {
      previousItemCount,
      expectedItemCount,
      baselineSource,
      baselineCountConflict: renderedCounts.baselineCountConflict,
      drawerItemCount: renderedCounts.drawerItemCount,
      headerItemCount: renderedCounts.headerItemCount,
      storedItemCount,
    };
  };

  const rememberCartState = (cart, context = {}) => {
    const itemCount = Number((cart && cart.item_count) ?? 0);
    const lastKnownCart = readLastKnownCart();
    const previousItemCount = toItemCount(context.previousItemCount);
    const expectedItemCount = toItemCount(context.expectedItemCount);
    const countRegressedAfterAdd =
      context.operation === 'add' &&
      itemCount > 0 &&
      expectedItemCount !== null &&
      itemCount < expectedItemCount &&
      (!context.addRecovery ||
        !Object.prototype.hasOwnProperty.call(context.addRecovery, 'countRegressionConfirmation') ||
        context.addRecovery.countRegressionConfirmation === 'confirmed');

    if (countRegressedAfterAdd) {
      reportFailure('cart_count_regressed_after_add', {
        source: context.source || 'product-form',
        operation: 'add',
        requestedQuantity: context.requestedQuantity,
        previousItemCount,
        expectedItemCount,
        baselineSource: context.baselineSource || '',
        baselineCountConflict: !!context.baselineCountConflict,
        drawerItemCount: context.drawerItemCount,
        headerItemCount: context.headerItemCount,
        storedItemCount: context.storedItemCount,
        addedLinePresent:
          context.addedLinePresent === true ? true : context.addedLinePresent === false ? false : null,
        addRecovery: context.addRecovery || null,
        cartResponse: context.cartResponse || null,
        expectedNonEmpty: true,
        lastKnownCart,
        currentCart: summarizeCart(cart),
      });
    }

    if (itemCount > 0) {
      writeLastKnownCart(cart, context);
      return;
    }

    const lastKnownCount = Number((lastKnownCart && lastKnownCart.cart && lastKnownCart.cart.item_count) ?? 0);
    if (context.intentionalEmpty) {
      clearLastKnownCart();
      return;
    }

    const expectedNonEmpty = !!context.expectedNonEmpty || Number(context.expectedItemCount) > 0;
    if (expectedNonEmpty || lastKnownCount > 0) {
      const emptyEventName = navigator.cookieEnabled === false
        ? 'cart_unavailable_cookies_disabled'
        : 'unexpected_empty_cart';
      reportFailure(emptyEventName, {
        source: context.source || 'cart-state',
        operation: context.operation || '',
        requestedQuantity: context.requestedQuantity,
        previousLineQuantity: context.previousLineQuantity,
        previousItemCount: context.previousItemCount,
        expectedItemCount: context.expectedItemCount,
        baselineSource: context.baselineSource || '',
        baselineCountConflict: !!context.baselineCountConflict,
        drawerItemCount: context.drawerItemCount,
        headerItemCount: context.headerItemCount,
        storedItemCount: context.storedItemCount,
        addedLinePresent:
          context.addedLinePresent === true ? true : context.addedLinePresent === false ? false : null,
        addRecovery: context.addRecovery || null,
        cartResponse: context.cartResponse || null,
        expectedNonEmpty,
        lastKnownCart,
        currentCart: summarizeCart(cart),
      });
    }
  };

  return {
    captureAddBaseline,
    rememberCartState,
    reportFailure,
    reportRecovery,
    getLastKnownCart: readLastKnownCart,
  };
}

window.SPCLSTCartMonitor = window.SPCLSTCartMonitor || createSPCLSTCartMonitor();

function createSPCLSTCartSession() {
  const getDebug = () => window.SPCLSTCartDebug;
  const canonicalCartCookieDomain = 'spclst.com';
  const cartCookieInspectionTimeoutMs = 200;
  let cartMutationVersion = 0;

  const log = (label, details = {}) => {
    const debug = getDebug();
    if (debug && debug.enabled()) debug.log(label, details);
  };

  const getLocaleRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root) return '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getRouteUrl = (routeName, endpoint) => {
    const configuredRoute = window.routes && window.routes[routeName];
    return new URL(configuredRoute || `${getLocaleRoot()}${endpoint}`, window.location.origin).toString();
  };

  const getCartJsonUrl = () => {
    const url = new URL(`${getLocaleRoot()}cart.js`, window.location.origin);
    url.searchParams.set('_', Date.now().toString());
    return url.toString();
  };

  const decodeCartCookieValue = (value) => {
    try {
      return decodeURIComponent(String(value || ''));
    } catch (error) {
      return String(value || '');
    }
  };

  const normalizeCookieDomain = (domain) =>
    String(domain || '')
      .trim()
      .toLowerCase()
      .replace(/^\./, '');

  const createCartCookieConflictState = (overrides = {}) => ({
    eligible: false,
    reason: 'unknown',
    supported: false,
    readSucceeded: false,
    timedOut: false,
    cookieCount: null,
    keyCount: null,
    distinctValueCount: null,
    hostOnlyCount: null,
    domainScopedCount: null,
    rootPathCount: null,
    partitionedCount: null,
    deleteOptions: null,
    ...overrides,
  });

  const inspectDuplicateCartCookieConflict = () => {
    const cookieStoreApi = window.cookieStore;
    if (
      !cookieStoreApi ||
      typeof cookieStoreApi.getAll !== 'function' ||
      typeof cookieStoreApi.delete !== 'function'
    ) {
      return Promise.resolve(createCartCookieConflictState({ reason: 'unsupported' }));
    }

    const timeout = new Promise((resolve) =>
      window.setTimeout(
        () =>
          resolve(
            createCartCookieConflictState({
              reason: 'read_timeout',
              supported: true,
              timedOut: true,
            })
          ),
        cartCookieInspectionTimeoutMs
      )
    );
    const read = Promise.resolve()
      .then(() => cookieStoreApi.getAll('cart'))
      .then((cookies) => ({ cookies }))
      .catch(() => ({ readFailed: true }));

    return Promise.race([read, timeout]).then((result) => {
      if (result && result.reason === 'read_timeout') return result;
      if (!result || result.readFailed) {
        return createCartCookieConflictState({
          reason: 'read_failed',
          supported: true,
        });
      }

      const cartCookies = Array.isArray(result.cookies) ? result.cookies : [];
      const values = cartCookies.map((cookie) => decodeCartCookieValue(cookie && cookie.value));
      const keyCount = values.filter((value) => value.includes('?key=')).length;
      const distinctValueCount = new Set(values).size;
      const scopeMetadataAvailable = cartCookies.every(
        (cookie) => cookie && 'domain' in cookie && 'path' in cookie
      );
      const partitionMetadataAvailable = cartCookies.every(
        (cookie) => cookie && 'partitioned' in cookie
      );
      const hostOnlyCount = scopeMetadataAvailable
        ? cartCookies.filter((cookie) => !normalizeCookieDomain(cookie.domain)).length
        : null;
      const domainCookies = scopeMetadataAvailable
        ? cartCookies.filter((cookie) => !!normalizeCookieDomain(cookie.domain))
        : [];
      const domainScopedCount = scopeMetadataAvailable ? domainCookies.length : null;
      const rootPathCount = scopeMetadataAvailable
        ? cartCookies.filter((cookie) => String(cookie.path || '/') === '/').length
        : null;
      const partitionedCount = partitionMetadataAvailable
        ? cartCookies.filter((cookie) => cookie.partitioned === true).length
        : null;
      const state = createCartCookieConflictState({
        supported: true,
        readSucceeded: true,
        cookieCount: cartCookies.length,
        keyCount,
        distinctValueCount,
        hostOnlyCount,
        domainScopedCount,
        rootPathCount,
        partitionedCount,
      });

      if (cartCookies.length !== 2) return { ...state, reason: 'cookie_count' };
      if (!scopeMetadataAvailable) return { ...state, reason: 'scope_metadata_unavailable' };
      if (!partitionMetadataAvailable) return { ...state, reason: 'partition_metadata_unavailable' };
      if (distinctValueCount !== 2) return { ...state, reason: 'cookie_values_match' };
      if (keyCount !== 2) return { ...state, reason: 'missing_key_parameter' };
      if (hostOnlyCount !== 1 || domainScopedCount !== 1) return { ...state, reason: 'scope_shape' };
      if (rootPathCount !== 2) return { ...state, reason: 'non_root_path' };
      if (partitionedCount !== 0) return { ...state, reason: 'partitioned_cookie' };
      if (normalizeCookieDomain(domainCookies[0].domain) !== canonicalCartCookieDomain) {
        return { ...state, reason: 'unexpected_domain' };
      }

      return {
        ...state,
        eligible: true,
        reason: 'eligible',
        deleteOptions: {
          name: 'cart',
          domain: canonicalCartCookieDomain,
          path: '/',
        },
      };
    });
  };

  const fetchCartJson = () =>
    fetch(getCartJsonUrl(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart JSON request failed with status ${response.status}`);
      return response.json();
    });

  const sleep = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));

  const getAddResponse = (details = {}) => details.addResponse || {};

  const getAddedVariantId = (details = {}) => {
    const response = getAddResponse(details);
    return String(response.variant_id || details.variantId || details.id || '');
  };

  const getAddedLineKey = (details = {}) => String(getAddResponse(details).key || '');

  const getRequestedQuantity = (details = {}) => {
    const quantity = Number(details.quantity || 1);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getAddedTargetQuantity = (details = {}) => {
    const responseQuantity = Number(getAddResponse(details).quantity);
    return Number.isFinite(responseQuantity) && responseQuantity > 0 ? responseQuantity : getRequestedQuantity(details);
  };

  const createAddRecoveryDiagnostics = (details = {}) => ({
    addResponseKeyPresent: !!getAddedLineKey(details),
    canonicalReadCount: 0,
    cookieConflictInspection: 'not_needed',
    cookieConflictDeletion: 'not_needed',
    cookieConflictVerification: 'not_needed',
    exactLineConfirmation: 'not_needed',
    addRetry: 'not_needed',
    outcome: 'pending',
  });

  const getAddRecoveryDiagnostics = (details = {}) => details.recoveryDiagnostics || null;

  const setAddRecoveryDiagnostic = (details, name, value) => {
    const diagnostics = getAddRecoveryDiagnostics(details);
    if (diagnostics) diagnostics[name] = value;
  };

  const fetchCartJsonForAdd = (details = {}) => {
    const diagnostics = getAddRecoveryDiagnostics(details);
    if (diagnostics) diagnostics.canonicalReadCount += 1;
    return fetchCartJson();
  };

  const normalizeLineProperties = (properties) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return [];

    return Object.keys(properties)
      .sort()
      .map((name) => [name, String(properties[name] == null ? '' : properties[name])]);
  };

  const getSellingPlanId = (line = {}) => {
    const allocation = line.selling_plan_allocation || {};
    const sellingPlan = allocation.selling_plan || {};
    return String(sellingPlan.id || line.selling_plan || '');
  };

  const lineIdentityMatches = (line, details = {}) => {
    const response = getAddResponse(details);
    return (
      JSON.stringify(normalizeLineProperties(line && line.properties)) ===
        JSON.stringify(normalizeLineProperties(response.properties)) &&
      getSellingPlanId(line) === getSellingPlanId(response)
    );
  };

  const getAddedLineCandidates = (cart, details = {}) => {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const variantId = getAddedVariantId(details);
    if (!variantId) return [];

    return items.filter(
      (item) => String(item.variant_id || item.id) === variantId && lineIdentityMatches(item, details)
    );
  };

  const findAddedLine = (cart, details = {}) => {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const lineKey = getAddedLineKey(details);
    if (lineKey) {
      const exactLine = items.find((item) => String(item.key || '') === lineKey);
      if (exactLine) return exactLine;
    }

    const identityMatches = getAddedLineCandidates(cart, details);
    return identityMatches.length === 1 ? identityMatches[0] : null;
  };

  const cartHasAddedLine = (cart, details = {}) => {
    const variantId = getAddedVariantId(details);
    if (!variantId) return Number((cart && cart.item_count) || 0) > 0;

    const addedLine = findAddedLine(cart, details);
    return !!addedLine && Number(addedLine.quantity || 0) >= getAddedTargetQuantity(details);
  };

  const operationIsCurrent = (operationVersion) => operationVersion === cartMutationVersion;

  const hostOnlyCartCookieWasPreserved = (state) =>
    !!state &&
    state.supported === true &&
    state.readSucceeded === true &&
    state.timedOut === false &&
    state.cookieCount === 1 &&
    state.keyCount === 1 &&
    state.distinctValueCount === 1 &&
    state.hostOnlyCount === 1 &&
    state.domainScopedCount === 0 &&
    state.rootPathCount === 1 &&
    state.partitionedCount === 0;

  const recoverProvenDuplicateCartCookieConflict = async (details = {}, operationVersion) => {
    const conflictState = await inspectDuplicateCartCookieConflict();
    setAddRecoveryDiagnostic(details, 'cookieConflictInspection', conflictState.reason);
    if (!conflictState.eligible) return { attempted: false, verified: false };

    if (!operationIsCurrent(operationVersion)) {
      setAddRecoveryDiagnostic(details, 'cookieConflictDeletion', 'skipped_newer_mutation');
      return { attempted: false, verified: false, skippedForNewerMutation: true };
    }

    const cookieStoreApi = window.cookieStore;
    setAddRecoveryDiagnostic(details, 'cookieConflictDeletion', 'attempted');
    try {
      await cookieStoreApi.delete(conflictState.deleteOptions);
      setAddRecoveryDiagnostic(details, 'cookieConflictDeletion', 'completed');
    } catch (error) {
      setAddRecoveryDiagnostic(details, 'cookieConflictDeletion', 'error');
      setAddRecoveryDiagnostic(details, 'cookieConflictVerification', 'not_run');
      log('cart session duplicate-cookie deletion failed', {
        source: details.source || 'product-form:add',
        error: error.message,
      });
      return { attempted: true, verified: false, error: error.message };
    }

    const postDeleteState = await inspectDuplicateCartCookieConflict();
    const verified = hostOnlyCartCookieWasPreserved(postDeleteState);
    setAddRecoveryDiagnostic(
      details,
      'cookieConflictVerification',
      verified ? 'host_only_preserved' : `failed_${postDeleteState.reason}`
    );

    if (!verified) {
      log('cart session duplicate-cookie deletion was not verified', {
        source: details.source || 'product-form:add',
        cookieState: postDeleteState,
      });
    }

    return {
      attempted: true,
      verified,
      skippedForNewerMutation: !operationIsCurrent(operationVersion),
    };
  };

  const fetchCartJsonUntilAddedLineAppears = (details = {}, operationVersion) => {
    const delays = [0, 160, 360, 720];

    return delays
      .reduce(
        (promise, delay) =>
          promise.then((cart) => {
            if (cart && cartHasAddedLine(cart, details)) return cart;
            if (!operationIsCurrent(operationVersion)) return cart;
            if (delay > 0) return sleep(delay).then(() => fetchCartJsonForAdd(details));
            return fetchCartJsonForAdd(details);
          }),
        Promise.resolve(null)
      );
  };

  const parseCartResponse = (response, requestLabel) =>
    response
      .json()
      .catch(() => ({}))
      .then((payload) => {
        if (response.ok && !payload.status) return payload;

        const description = payload.description || payload.message || `${requestLabel} failed with status ${response.status}`;
        const error = new Error(description);
        error.status = response.status;
        error.description = description;
        throw error;
      });

  const changeAddedLineQuantity = (lineKey, quantity) =>
    fetch(getRouteUrl('cart_change_url', 'cart/change.js'), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: String(lineKey), quantity }),
    }).then((response) => parseCartResponse(response, 'Cart line confirmation'));

  const buildRetryAddFormData = (details = {}) => {
    const formData = new FormData();

    (Array.isArray(details.formDataEntries) ? details.formDataEntries : []).forEach(([name, value]) => {
      if (!name || name === 'sections' || name === 'sections_url') return;
      formData.append(name, value);
    });

    if (!formData.has('id') && getAddedVariantId(details)) formData.append('id', getAddedVariantId(details));
    if (!formData.has('quantity')) formData.append('quantity', String(getRequestedQuantity(details)));
    return formData;
  };

  const retryJustAddedLine = (details = {}, operationVersion) => {
    if (!operationIsCurrent(operationVersion)) {
      setAddRecoveryDiagnostic(details, 'outcome', 'superseded');
      return Promise.resolve({ recovered: false, skippedForNewerMutation: true, cart: null });
    }

    setAddRecoveryDiagnostic(details, 'addRetry', 'attempted');

    return fetch(getRouteUrl('cart_add_url', 'cart/add.js'), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: buildRetryAddFormData(details),
    })
      .then((response) => parseCartResponse(response, 'Cart add retry'))
      .then((addResponse) => {
        const retryDetails = { ...details, addResponse };
        return fetchCartJsonUntilAddedLineAppears(retryDetails, operationVersion).then((cart) => {
          const recovered = operationIsCurrent(operationVersion) && cartHasAddedLine(cart, retryDetails);
          setAddRecoveryDiagnostic(details, 'addRetry', recovered ? 'recovered' : 'still_missing');
          setAddRecoveryDiagnostic(details, 'outcome', recovered ? 'recovered_by_add_retry' : 'missing_after_add_retry');
          return { recovered, missingAddedLine: !recovered, recovery: 'add-retry', cart };
        });
      });
  };

  const isMissingLineError = (error) =>
    Number(error && error.status) === 400 &&
    String((error && (error.description || error.message)) || '')
      .toLowerCase()
      .includes('no valid id or line parameter');

  const recoverJustAddedLine = async (details = {}, staleCart, operationVersion) => {
    if (!operationIsCurrent(operationVersion)) {
      setAddRecoveryDiagnostic(details, 'outcome', 'superseded');
      return { recovered: false, skippedForNewerMutation: true, cart: staleCart };
    }

    const staleLine = findAddedLine(staleCart, details);
    const lineKey = String((staleLine && staleLine.key) || getAddedLineKey(details) || '');
    if (!lineKey) {
      setAddRecoveryDiagnostic(details, 'exactLineConfirmation', 'unavailable');
      setAddRecoveryDiagnostic(details, 'outcome', 'missing_without_line_key');
      return { recovered: false, missingAddedLine: true, cart: staleCart };
    }

    setAddRecoveryDiagnostic(details, 'exactLineConfirmation', 'attempted');
    try {
      const changedCart = await changeAddedLineQuantity(lineKey, getAddedTargetQuantity(details));
      const recovered = operationIsCurrent(operationVersion) && cartHasAddedLine(changedCart, details);
      setAddRecoveryDiagnostic(details, 'exactLineConfirmation', recovered ? 'recovered' : 'still_missing');
      setAddRecoveryDiagnostic(
        details,
        'outcome',
        recovered ? 'recovered_by_line_confirmation' : 'missing_after_line_confirmation'
      );
      return { recovered, missingAddedLine: !recovered, recovery: 'line-change', cart: changedCart };
    } catch (error) {
      if (!isMissingLineError(error)) {
        setAddRecoveryDiagnostic(details, 'exactLineConfirmation', 'error');
        setAddRecoveryDiagnostic(details, 'outcome', 'line_confirmation_error');
        log('cart session exact-line confirmation failed', {
          source: details.source || 'product-form:add',
          status: error.status || null,
          error: error.message,
        });
        return { recovered: false, missingAddedLine: true, cart: staleCart, error: error.message };
      }

      setAddRecoveryDiagnostic(details, 'exactLineConfirmation', 'line_key_missing');
    }

    if (!operationIsCurrent(operationVersion)) {
      setAddRecoveryDiagnostic(details, 'outcome', 'superseded');
      return { recovered: false, skippedForNewerMutation: true, cart: staleCart };
    }

    const latestCart = await fetchCartJsonForAdd(details);
    if (cartHasAddedLine(latestCart, details)) {
      setAddRecoveryDiagnostic(details, 'outcome', 'confirmed_after_line_key_missing');
      return { recovered: false, cart: latestCart };
    }

    const latestLine = findAddedLine(latestCart, details);
    if (latestLine) {
      const latestLineKey = String(latestLine.key || '');
      if (latestLineKey && latestLineKey !== lineKey && operationIsCurrent(operationVersion)) {
        setAddRecoveryDiagnostic(details, 'exactLineConfirmation', 'current_key_attempted');
        try {
          const changedCart = await changeAddedLineQuantity(latestLineKey, getAddedTargetQuantity(details));
          const recovered = operationIsCurrent(operationVersion) && cartHasAddedLine(changedCart, details);
          setAddRecoveryDiagnostic(details, 'exactLineConfirmation', recovered ? 'current_key_recovered' : 'current_key_still_missing');
          setAddRecoveryDiagnostic(
            details,
            'outcome',
            recovered ? 'recovered_by_current_key' : 'missing_after_current_key_confirmation'
          );
          return { recovered, missingAddedLine: !recovered, recovery: 'current-line-change', cart: changedCart };
        } catch (error) {
          setAddRecoveryDiagnostic(details, 'exactLineConfirmation', 'current_key_error');
          setAddRecoveryDiagnostic(details, 'outcome', 'current_key_confirmation_error');
          log('cart session current-line confirmation failed', {
            source: details.source || 'product-form:add',
            status: error.status || null,
            error: error.message,
          });
        }
      }

      const diagnostics = getAddRecoveryDiagnostics(details);
      if (diagnostics && diagnostics.outcome === 'pending') {
        setAddRecoveryDiagnostic(details, 'outcome', 'matching_line_below_target');
      }
      return { recovered: false, missingAddedLine: true, cart: latestCart };
    }

    const ambiguousLines = getAddedLineCandidates(latestCart, details);
    if (ambiguousLines.length > 1) {
      setAddRecoveryDiagnostic(details, 'addRetry', 'skipped_ambiguous');
      setAddRecoveryDiagnostic(details, 'outcome', 'ambiguous_matching_lines');
      log('cart session skipped retry for ambiguous matching lines', {
        source: details.source || 'product-form:add',
        matchingLineCount: ambiguousLines.length,
      });
      return { recovered: false, missingAddedLine: true, ambiguousAddedLine: true, cart: latestCart };
    }

    // Retry only the just-submitted line after Shopify confirms its original line key is absent.
    try {
      return await retryJustAddedLine(details, operationVersion);
    } catch (error) {
      setAddRecoveryDiagnostic(details, 'addRetry', 'error');
      setAddRecoveryDiagnostic(details, 'outcome', 'add_retry_error');
      log('cart session just-added line retry failed', {
        source: details.source || 'product-form:add',
        status: error.status || null,
        error: error.message,
      });
      return { recovered: false, missingAddedLine: true, cart: latestCart, error: error.message };
    }
  };

  const confirmJustAddedLine = (details = {}, operationVersion) =>
    fetchCartJsonUntilAddedLineAppears(details, operationVersion).then(async (cart) => {
      if (!operationIsCurrent(operationVersion)) {
        setAddRecoveryDiagnostic(details, 'outcome', 'superseded');
        return { recovered: false, skippedForNewerMutation: true, cart };
      }
      if (cartHasAddedLine(cart, details)) {
        setAddRecoveryDiagnostic(details, 'outcome', 'confirmed');
        return { recovered: false, cart };
      }

      const variantId = getAddedVariantId(details);
      if (!variantId) {
        setAddRecoveryDiagnostic(details, 'outcome', 'unidentified_add_response');
        return { recovered: false, cart };
      }

      log('cart session missing just-added line', {
        source: details.source || 'product-form:add',
        variantId,
        cart: getDebug() ? getDebug().summarizeCartJson(cart) : null,
      });

      const cookieConflictRecovery = await recoverProvenDuplicateCartCookieConflict(details, operationVersion);
      if (cookieConflictRecovery.skippedForNewerMutation) {
        setAddRecoveryDiagnostic(details, 'outcome', 'superseded');
        return { recovered: false, skippedForNewerMutation: true, cart };
      }

      if (cookieConflictRecovery.verified) {
        const canonicalCart = await fetchCartJsonForAdd(details);
        if (!operationIsCurrent(operationVersion)) {
          setAddRecoveryDiagnostic(details, 'outcome', 'superseded');
          return { recovered: false, skippedForNewerMutation: true, cart: canonicalCart };
        }
        if (cartHasAddedLine(canonicalCart, details)) {
          setAddRecoveryDiagnostic(details, 'outcome', 'recovered_by_cookie_cleanup');
          return {
            recovered: true,
            recovery: 'duplicate-cookie-cleanup',
            cart: canonicalCart,
          };
        }

        return recoverJustAddedLine(details, canonicalCart, operationVersion);
      }

      return recoverJustAddedLine(details, cart, operationVersion);
    });

  const confirmCountRegressionAfterAdd = async (details = {}, cart, operationVersion) => {
    const expectedItemCount = Number(details.expectedItemCount);
    const currentItemCount = Number((cart && cart.item_count) || 0);
    const needsConfirmation =
      Number.isFinite(expectedItemCount) &&
      expectedItemCount > 0 &&
      currentItemCount > 0 &&
      currentItemCount < expectedItemCount &&
      cartHasAddedLine(cart, details);

    if (!needsConfirmation) return { cart, skippedForNewerMutation: false };

    await sleep(320);
    if (!operationIsCurrent(operationVersion)) {
      setAddRecoveryDiagnostic(details, 'countRegressionConfirmation', 'skipped_newer_mutation');
      return { cart, skippedForNewerMutation: true };
    }

    try {
      const confirmedCart = await fetchCartJsonForAdd(details);
      if (!operationIsCurrent(operationVersion)) {
        setAddRecoveryDiagnostic(details, 'countRegressionConfirmation', 'skipped_newer_mutation');
        return { cart: confirmedCart, skippedForNewerMutation: true };
      }

      const confirmedItemCount = Number(confirmedCart && confirmedCart.item_count);
      if (!Number.isInteger(confirmedItemCount) || confirmedItemCount < 0 || !Array.isArray(confirmedCart.items)) {
        throw new Error('Count-regression confirmation did not return a valid cart');
      }

      const recovered = confirmedItemCount >= expectedItemCount && cartHasAddedLine(confirmedCart, details);
      setAddRecoveryDiagnostic(details, 'countRegressionConfirmation', recovered ? 'recovered' : 'confirmed');
      return { cart: confirmedCart, skippedForNewerMutation: false };
    } catch (error) {
      setAddRecoveryDiagnostic(details, 'countRegressionConfirmation', 'error');
      log('cart session count-regression confirmation failed', {
        source: details.source || 'product-form:add',
        error: error.message,
      });
      return { cart, skippedForNewerMutation: false };
    }
  };

  const rememberCartState = (cart, context = {}) => {
    if (window.SPCLSTCartMonitor && typeof window.SPCLSTCartMonitor.rememberCartState === 'function') {
      window.SPCLSTCartMonitor.rememberCartState(cart, context);
    }

    log('cart session observed cart state', {
      source: context.source || 'cart-state',
      cart: getDebug() ? getDebug().summarizeCartJson(cart) : null,
    });
    return cart;
  };

  const noteCartMutation = (context = {}) => {
    cartMutationVersion += 1;
    log('cart session newer mutation observed', {
      source: context.source || 'cart-mutation',
    });
    return cartMutationVersion;
  };

  const syncAfterAdd = (details = {}) => {
    const operationVersion = noteCartMutation({ source: `${details.source || 'product-form'}:add` });
    const recoveryDiagnostics = createAddRecoveryDiagnostics(details);
    const diagnosticDetails = { ...details, recoveryDiagnostics };

    return confirmJustAddedLine(diagnosticDetails, operationVersion)
      .then(async (result) => {
        if (result.skippedForNewerMutation) {
          return { ...result, addRecovery: { ...recoveryDiagnostics } };
        }
        const countConfirmation = await confirmCountRegressionAfterAdd(
          diagnosticDetails,
          result.cart,
          operationVersion
        );
        if (countConfirmation.skippedForNewerMutation) {
          return {
            ...result,
            cart: countConfirmation.cart,
            skippedForNewerMutation: true,
            addRecovery: { ...recoveryDiagnostics },
          };
        }
        const cart = countConfirmation.cart;
        if (result.recovery || result.ambiguousAddedLine) {
          log('cart session just-added recovery result', {
            source: details.source || 'product-form:add',
            recovery: result.recovery || null,
            recovered: !!result.recovered,
            ambiguousAddedLine: !!result.ambiguousAddedLine,
            cart: getDebug() ? getDebug().summarizeCartJson(cart) : null,
          });
        }
        const addRecovery = { ...recoveryDiagnostics };
        if (
          Number((cart && cart.item_count) || 0) > 0 &&
          addRecovery.cookieConflictDeletion === 'completed' &&
          addRecovery.cookieConflictVerification === 'host_only_preserved' &&
          window.SPCLSTCartMonitor &&
          typeof window.SPCLSTCartMonitor.reportRecovery === 'function'
        ) {
          window.SPCLSTCartMonitor.reportRecovery('duplicate_cart_cookie_recovered', {
            source: details.source || 'product-form:add',
            operation: 'add',
            expectedNonEmpty: true,
            addRecovery,
            currentCart: { item_count: Number(cart.item_count || 0) },
          });
        }
        rememberCartState(cart, {
          source: details.source || 'product-form:add',
          operation: 'add',
          requestedQuantity: getRequestedQuantity(details),
          previousItemCount: details.previousItemCount,
          expectedItemCount: details.expectedItemCount,
          baselineSource: details.baselineSource,
          baselineCountConflict: details.baselineCountConflict,
          drawerItemCount: details.drawerItemCount,
          headerItemCount: details.headerItemCount,
          storedItemCount: details.storedItemCount,
          addedLinePresent: cartHasAddedLine(cart, diagnosticDetails),
          expectedNonEmpty: true,
          addRecovery,
        });
        return { ...result, cart, addRecovery };
      })
      .catch((error) => {
        recoveryDiagnostics.outcome = 'sync_error';
        log('cart session sync after add failed', {
          source: details.source || 'product-form:add',
          error: error.message,
        });
        return { recovered: false, error: error.message, addRecovery: { ...recoveryDiagnostics } };
      });
  };

  const ensureBeforeCheckout = (details = {}) =>
    fetchCartJson()
      .then((cart) => {
        rememberCartState(cart, { source: details.source || 'checkout' });
        return { recovered: false, cart };
      })
      .catch((error) => {
        log('cart session checkout fetch failed', {
          source: details.source || 'checkout',
          error: error.message,
        });
        return { recovered: false, error: error.message };
      });

  return {
    syncAfterAdd,
    ensureBeforeCheckout,
    rememberCartState,
    noteCartMutation,
    fetchCartJson,
    inspectDuplicateCartCookieConflict,
  };
}

window.SPCLSTCartSession = window.SPCLSTCartSession || createSPCLSTCartSession();

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
